import { Fragment, h, VNode } from 'preact';
import { useState, useCallback, useEffect, useMemo } from 'preact/compat';
import axios from 'axios';
import Button from './components/Button';
import './assets/styles/main.scss';
import './style.scss';
import Modal from './components/Modal';
import api from './utils/api';
import { sendAnalytics } from './utils/amplitude';
import {
  AssistButton,
  AssistButtonAction,
  AssistButtonDeferredAction,
  AssistReason,
  AssistRenderType,
  AssistResponse,
  DataLoadingStatus,
} from './types';
import Spinner from './components/Spinner';
import SocketHandler from './components/SocketHandler';
import failureModalData from './utils/failureModalData';
import { stepByRenderType, usePrevious } from './utils';

interface Props {
  title?: string;
  userToken: string;
  organizationToken: string;
  requestId: number;
  requestStatus: string;
  dspName: string;
  organizationId: string;
  source: string;
  lang: string;
  isDashboard: number;
  username: string;
}

const isTheSame = (obj1, obj2) => JSON.stringify(obj1) === JSON.stringify(obj2);

function Widget({
  userToken = '',
  organizationToken = '',
  title,
  requestId: initialRequestId,
  requestStatus,
  dspName,
  source,
  organizationId,
  lang,
  isDashboard,
  username,
}: Props): VNode {
  const [buttonData, setButtonData] = useState<AssistButton>({ label: title, action: 'help', type: 'main_default' });
  const [modalData, setModalData] = useState<AssistResponse>(null);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [dataLoadingStatus, setDataLoadingStatus] = useState<DataLoadingStatus>(DataLoadingStatus.Initial);
  const [renderType, setRenderType] = useState<AssistRenderType>(undefined);
  const [requestId, setRequestId] = useState(Number(initialRequestId));

  const [abortController] = useState(new AbortController());
  const [bgUpdateAbortController, setBgUpdateAbortController] = useState(new AbortController());

  const prevRequestId = usePrevious(requestId);
  const prevModalData = usePrevious(modalData);
  const prevDataLoadingStatus = usePrevious(dataLoadingStatus);

  useEffect(() => {
    if (userToken) {
      axios.defaults.headers.common['Token-User'] = userToken;
    }
    if (organizationToken) {
      axios.defaults.headers.common['Token-Organization'] = organizationToken;
    }
    axios.defaults.headers.common['Accept-Language'] = lang;
  }, [lang, organizationToken, userToken]);

  useEffect(() => {
    const hideModal = () => setModalData(null);
    addEventListener('popstate', hideModal);
    return () => {
      removeEventListener('popstate', hideModal);
    };
  }, []);

  const hideModal = useCallback(() => {
    setModalData(null);
    setRenderType(undefined);
    // Hard rerender main button
    // @ts-ignore
    if (!document.querySelector('[data-widget-host="taker-assist"]')?._habitat) {
      const force = true;
      // @ts-ignore
      window.renderAssist(force);
    }
  }, []);

  const checkRequestId = useCallback(
    (data: AssistResponse) => {
      if (data.delivery_request_id && requestId !== data.delivery_request_id) {
        setRequestId(data.delivery_request_id);
      }
    },
    [requestId]
  );

  const handleResponse = useCallback(
    (data: AssistResponse) => {
      switch (data.render_type) {
        case 'close':
          hideModal();
          break;
        case 'popup_help':
        case 'popup_reason':
        case 'popup_icon':
          if (isTheSame(modalData, data)) {
            return;
          }

          setModalData(data);
          setRenderType(data?.render_type);
          break;
        case 'button':
          if (isTheSame(buttonData, data.content.buttons[0])) {
            return;
          }
          setButtonData(data.content.buttons[0]);
          break;
        default:
          break;
      }
    },
    [buttonData, hideModal, modalData]
  );

  const handleGetButton = useCallback(() => {
    const asyncGetButton = async () => {
      if (buttonLoading) {
        return;
      }
      setButtonLoading(true);

      try {
        const data: AssistResponse = await api.fetchButton({ requestId }, { signal: abortController.signal });
        handleResponse(data);
      } catch (error) {
        console.log(error);
      }

      setButtonLoading(false);
    };
    asyncGetButton();
  }, [abortController.signal, buttonLoading, handleResponse, requestId]);

  const checkBackgroundUpdate = useCallback(() => {
    const asyncBackgroundUpdate = async () => {
      bgUpdateAbortController.abort();
      const _bgUpdateAbortController = new AbortController();
      setBgUpdateAbortController(_bgUpdateAbortController);

      try {
        const data = await api.fetchBackgroundUpdate({ requestId }, { signal: _bgUpdateAbortController.signal });

        checkRequestId(data);
        if (dataLoadingStatus !== DataLoadingStatus.Loading && !!modalData) {
          handleResponse(data);
        }
      } catch (error) {
        console.log(error);
        if (!_bgUpdateAbortController.signal.aborted) {
          setDataLoadingStatus(DataLoadingStatus.Failure);
        }
      }
    };
    asyncBackgroundUpdate();
  }, [bgUpdateAbortController, checkRequestId, dataLoadingStatus, handleResponse, modalData, requestId]);

  useEffect(() => {
    handleGetButton();
    return () => {
      abortController.abort();
      bgUpdateAbortController.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      // (prevRequestId && prevRequestId !== requestId) || // requestId was changed
      (prevModalData && modalData === null) || // modal was closed
      (prevDataLoadingStatus !== dataLoadingStatus &&
        dataLoadingStatus === DataLoadingStatus.Loaded &&
        prevModalData !== null &&
        modalData !== null) // modal was updated
    ) {
      handleGetButton();
    }
  }, [dataLoadingStatus, handleGetButton, modalData, prevDataLoadingStatus, prevModalData, prevRequestId, requestId]);

  const handleGetNextStep = useCallback(
    ({
      action,
      reason,
      message,
      deferredAction,
      renderType,
    }: {
      action: AssistButtonAction;
      reason?: AssistReason;
      message?: string;
      deferredAction?: AssistButtonDeferredAction;
      renderType: AssistRenderType;
    }) => {
      const asyncGetNextStep = async () => {
        sendAnalytics({
          action,
          reason,
          deferredAction,
          message,
          renderType,
          dspName,
          requestId,
          requestStatus,
          source,
        });

        if (action === 'close') {
          hideModal();
          return;
        }

        try {
          setDataLoadingStatus(DataLoadingStatus.Loading);
          const data: AssistResponse = await api.fetchModal(
            {
              requestId,
              requestStatus,
              action,
              reason,
              message,
              deferredAction,
              isDashboard,
              username,
            },
            { signal: abortController.signal }
          );

          handleResponse(data);
          checkRequestId(data);
          setDataLoadingStatus(DataLoadingStatus.Loaded);
        } catch (error) {
          console.log(error);
          handleResponse(failureModalData(lang));
          setDataLoadingStatus(DataLoadingStatus.Failure);
        }
      };

      asyncGetNextStep();
    },
    [
      abortController.signal,
      checkRequestId,
      dspName,
      handleResponse,
      hideModal,
      isDashboard,
      lang,
      requestId,
      requestStatus,
      source,
      username,
    ]
  );

  const handleGetFirstStep = useCallback(
    action => {
      handleGetNextStep({ action, renderType: 'button' });
    },
    [handleGetNextStep]
  );

  const handleSocketMessage = useCallback(() => {
    checkBackgroundUpdate();
    handleGetButton();
  }, [checkBackgroundUpdate, handleGetButton]);

  const StepComponent = useMemo(() => stepByRenderType(renderType), [renderType]);

  return (
    <Fragment>
      <SocketHandler
        dataLoadingStatus={dataLoadingStatus}
        organizationId={organizationId}
        requestStatus={requestStatus}
        requestId={requestId}
        onStatusChanged={handleSocketMessage}
      />
      <Button
        data={buttonData}
        onClick={handleGetFirstStep}
        loading={buttonLoading}
        className="button_widget btn-border-none"
        isWidgetButton
        lang={lang}
      />
      <Modal onMaskClick={hideModal} isOpen={!!modalData || dataLoadingStatus === DataLoadingStatus.Loading}>
        {dataLoadingStatus === DataLoadingStatus.Loading ? (
          <Spinner className="" size={148} container color="#429EFF" />
        ) : (
          <div className={`assist-modal modal-${String(renderType)}`}>
            {modalData && (
              <StepComponent
                onHideModal={hideModal}
                renderType={renderType}
                onNextStep={handleGetNextStep}
                content={modalData?.content}
                lang={lang}
              />
            )}
          </div>
        )}
      </Modal>
    </Fragment>
  );
}

const WidgetHOC = props => {
  const [show, setShow] = useState(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_, setTime] = useState(Date.now());

  useEffect(() => {
    // @ts-ignore
    window.unmountAssist = () => {
      setShow(false);
      setTimeout(() => {
        // @ts-ignore
        delete window.updateAssist;
        // @ts-ignore
        delete window.unmountAssist;
      }, 0);
    };

    // @ts-ignore
    window.updateAssist = () => {
      setTime(Date.now());
    };
  }, []);

  return show ? <Widget {...props} /> : null;
};

export default WidgetHOC;
